import React from "react";

import clsx from "clsx";
import { useSearchParams } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { v4 as uuidv4 } from "uuid";

import { GenericTooltip } from "@components/tooltip-container";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { useChartState } from "@features/sales-v3/hooks/use-chart-state";
import { useResetTabStates } from "@features/sales-v3/tabs/hooks/use-reset-tabs-state";
import { IconButton } from "@shared/buttons";

/* eslint-disable max-len */
export const ChartAggregateMetricIcon = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M14.2754 0.416016L15.2051 1.3457L9.51758 7.0332L6.01758 3.5332L1.72461 7.82617L0.794922 6.89648L6.01758 1.67383L9.51758 5.17383L14.2754 0.416016Z" />
    <path d="M14.2754 4.17383L15.2051 5.10352L9.51758 10.791L6.01758 7.29102L1.72461 11.584L0.794922 10.6543L6.01758 5.43164L9.51758 8.93164L14.2754 4.17383Z" />
    <path d="M14.2754 8.17383L15.2051 9.10352L9.51758 14.791L6.01758 11.291L1.72461 15.584L0.794922 14.6543L6.01758 9.43164L9.51758 12.9316L14.2754 8.17383Z" />
  </svg>
);

export const ChartSplitMetricIcon = (
  <svg
    width="16"
    height="8"
    viewBox="0 0 16 8"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M14.2754 0.294922L15.2051 1.22461L9.51758 6.91211L6.01758 3.41211L1.72461 7.70508L0.794922 6.77539L6.01758 1.55273L9.51758 5.05273L14.2754 0.294922Z" />
  </svg>
);
/* eslint-enable max-len */

interface ChartViewsButtonsProps {
  chartIndex: number;
}

export const ChartViewsButtons = ({ chartIndex }: ChartViewsButtonsProps) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { getChartAt, updateChartView, chartConfigs } = useChartState();
  const { chartView, comparisons } = getChartAt(chartIndex);
  const canChangeChartView = comparisons.length <= 1;
  const isAggregateMetric = chartView === "aggregate";
  const { addToast, removeToast, removeAllToasts } = useToasts();
  const [searchParams, setSearchParams] = useSearchParams();
  const { resetAllStates } = useResetTabStates();

  return (
    <div className="flex flex-col justify-between">
      <div className="font-bold text-xs mb-0.5">CHART VIEW</div>
      <div className="flex space-x-2">
        <GenericTooltip content="Aggregate" noInfoCircle mouseEnterDelay={0.1}>
          <IconButton
            className={clsx(
              {
                "fill-white bg-cosmic border-transparent": isAggregateMetric,
                "fill-interstellar hover:bg-cosmic-4 hover:border-cosmic+1":
                  !isAggregateMetric,
              },
              "p-2 border rounded-lg h-full",
            )}
            isDisabled={!canChangeChartView}
            onClick={() => {
              if (!canChangeChartView || chartView === "aggregate") {
                return;
              }

              prettyLogEventPage({
                domain: "Charts",
                action: "onClick",
                additionalActionInfo: "Change Chart View aggregate",
              });

              updateChartView(chartIndex, "aggregate");
              const stayOnSplit = chartConfigs
                .filter((_, i) => i !== chartIndex)
                .some((c) => c.chartView === "split");

              if (!stayOnSplit) {
                resetAllStates();
                removeAllToasts();
              }
            }}
          >
            {ChartSplitMetricIcon}
          </IconButton>
        </GenericTooltip>
        <GenericTooltip content="Split" noInfoCircle mouseEnterDelay={0.1}>
          <IconButton
            className={clsx(
              {
                "fill-white bg-cosmic border-transparent": !isAggregateMetric,
                "fill-interstellar hover:bg-cosmic-4 hover:border-cosmic+1":
                  isAggregateMetric,
              },
              "p-2 border rounded-lg h-full",
            )}
            isDisabled={!canChangeChartView}
            onClick={() => {
              if (!canChangeChartView || chartView === "split") {
                return;
              }

              prettyLogEventPage({
                domain: "Charts",
                action: "onClick",
                additionalActionInfo: "Change Chart View split",
              });

              updateChartView(chartIndex, "split");

              // if there is still going to be another split then do nothing, else change
              const stayOnSplit = chartConfigs
                .filter((_, i) => i !== chartIndex)
                .some((c) => c.chartView === "split");

              if (!stayOnSplit) {
                resetAllStates();
                removeAllToasts();
              }
            }}
          >
            {ChartAggregateMetricIcon}
          </IconButton>
        </GenericTooltip>
      </div>
    </div>
  );
};
